<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card report-card">
          <div class="card-body">
            <div class="report-header-bar">
              <div>
                <h5>Purchase Report</h5>
                <p>What you bought in a period, item by item, bill by bill or by category. Choose the filters and click View Report; the Excel and PDF downloads use the same filters.</p>
              </div>
              <div class="export-actions">
                <button class="btn btn-outline-success" @click="startExport('excel')" :disabled="exportLoading">
                  <vue-feather type="download" size="15"></vue-feather>
                  {{ exportLoading && exportJob.export_format === "excel" ? "Preparing..." : "Excel" }}
                </button>
                <button class="btn btn-outline-danger" @click="startExport('pdf')" :disabled="exportLoading">
                  <vue-feather type="file-text" size="15"></vue-feather>
                  {{ exportLoading && exportJob.export_format === "pdf" ? "Preparing..." : "PDF" }}
                </button>
              </div>
            </div>

            <div class="report-toolbar">
              <div class="filter-grid">
                <div class="form-group">
                  <label>From Date</label>
                  <b-form-input v-model="filters.startdate" type="date" />
                </div>
                <div class="form-group">
                  <label>To Date</label>
                  <b-form-input v-model="filters.enddate" type="date" />
                </div>
                <div class="form-group">
                  <label>Category</label>
                  <b-form-select v-model="filters.categoryid" :options="categoryOptions" />
                </div>
                <div class="form-group">
                  <label>Report Type</label>
                  <b-form-select v-model="filters.groupBy" :options="groupByOptions" />
                </div>
                <div class="button-group">
                  <button class="btn btn-submit" :disabled="loading" @click="generateReport">{{ loading ? "Loading..." : "View Report" }}</button>
                  <button class="btn btn-outline-secondary" @click="resetFilters">Reset</button>
                </div>
              </div>
              <ReportDatePresets :from="filters.startdate" :to="filters.enddate" @select="applyPeriod" />
            </div>

            <div v-if="exportJob.job_id" class="export-status" :class="`status-${exportJob.status}`">
              <div class="export-status-copy">
                <strong>{{ exportStatusTitle }}</strong>
                <span>{{ exportStatusText }}</span>
              </div>
              <button v-if="exportJob.status === 'completed'" class="btn btn-sm btn-download" @click="downloadExport">
                Download {{ exportJob.export_format === "pdf" ? "PDF" : "Excel" }}
              </button>
            </div>

            <div class="summary-strip">
              <div>
                <span>{{ filters.groupBy === "billwise" ? "Bills" : "Purchase Lines" }}</span>
                <strong>{{ summaryRowCount }}</strong>
              </div>
              <div>
                <span>Items Bought (Qty)</span>
                <strong>{{ formatNumber(summary.quantity) }}</strong>
              </div>
              <div>
                <span>Purchase Amount</span>
                <strong>₹{{ formatCurrency(summary.amount) }}</strong>
              </div>
              <div>
                <span>MRP Value</span>
                <strong>₹{{ formatCurrency(summary.saleamount) }}</strong>
              </div>
            </div>

            <div v-if="filters.groupBy === 'billwise'" class="billwise-sheet">
              <div v-if="loading" class="billwise-message">Loading bill-wise report...</div>
              <div v-else-if="!displayRows.length" class="billwise-message">No purchases found for the selected period. Try a wider date range or another category.</div>
              <section v-for="(bill, index) in pagedBills" :key="bill.rowkey" class="billwise-bill">
                <header class="billwise-bill-header">
                  <span class="bill-index">{{ billOffset + index + 1 }}</span>
                  <strong>{{ displayValue(bill.billno) }}</strong>
                  <strong class="bill-vendor">{{ bill.vendorname || "Vendor" }}</strong>
                  <span class="bill-meta">Inv: {{ displayValue(bill.invoiceno) }} | Date: {{ displayValue(bill.invoicedate) }}</span>
                  <span v-if="bill.gstin || bill.transportmode" class="bill-meta">
                    {{ bill.gstin ? `GST: ${bill.gstin}` : "" }}{{ bill.gstin && bill.transportmode ? " | " : "" }}{{ bill.transportmode ? `Lorry: ${bill.transportmode}` : "" }}
                  </span>
                  <span v-if="bill.address" class="bill-address">Addr: {{ bill.address }}</span>
                </header>
                <table class="billwise-items">
                  <thead>
                    <tr>
                      <th>Item Code</th>
                      <th>Item</th>
                      <th>HSN</th>
                      <th class="numeric">Qty</th>
                      <th class="numeric">Purchase Price</th>
                      <th class="numeric">Bill Amount</th>
                      <th class="numeric">MRP</th>
                      <th class="numeric">MRP Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, itemIndex) in bill.itemlines" :key="`${bill.rowkey}-item-${itemIndex}`">
                      <td>{{ displayValue(item.itemcode) }}</td>
                      <td>{{ displayValue(item.itemname) }}</td>
                      <td>{{ displayValue(item.hsncode) }}</td>
                      <td class="numeric">{{ formatNumber(item.quantity) }}</td>
                      <td class="numeric">{{ formatCurrency(item.purchaseprice) }}</td>
                      <td class="numeric">{{ formatCurrency(item.amount) }}</td>
                      <td class="numeric">{{ formatCurrency(item.saleprice) }}</td>
                      <td class="numeric">{{ formatCurrency(item.saleamount) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="3">Sub Total :</td>
                      <td class="numeric">{{ formatNumber(bill.quantity) }}</td>
                      <td></td>
                      <td class="numeric">{{ formatCurrency(bill.total) }}</td>
                      <td></td>
                      <td class="numeric">{{ formatCurrency(bill.saleamount) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </section>
              <div v-if="displayRows.length > billPageSize" class="report-pager">
                <a-pagination
                  v-model:current="billPage"
                  v-model:pageSize="billPageSize"
                  :total="displayRows.length"
                  show-size-changer
                  :page-size-options="['10', '25', '50', '100']"
                  :show-total="(total, range) => `${range[0]}-${range[1]} of ${total.toLocaleString('en-IN')} bills`"
                />
              </div>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="displayRows"
                :loading="loading"
                :pagination="tablePagination"
                row-key="rowkey"
                :row-class-name="rowClassName"
              >
                <template #bodyCell="{ column, record, index }">
                  <template v-if="record.rowtype === 'category_header' && column.key === 'slno' && filters.groupBy === 'itemwise'">
                    <span class="group-header-text">{{ displayValue(record.itemname) }}</span>
                  </template>
                  <template v-else-if="column.key === 'slno'">
                    {{ rowSerialNumber(record) }}
                  </template>
                  <template v-else-if="record.rowtype === 'category_header' && ['categoryname', 'itemname'].includes(column.key)">
                    <span class="group-header-text">{{ displayValue(filters.groupBy === 'itemwise' ? '' : record.categoryname) }}</span>
                  </template>
                  <template v-else-if="record.rowtype === 'category_header'">
                    {{ "" }}
                  </template>
                  <template v-else-if="record.rowtype === 'category_total' && ['categoryname', 'itemname'].includes(column.key)">
                    <span class="group-total-text">{{ displayValue(record[column.dataIndex]) }}</span>
                  </template>
                  <template v-else-if="record.rowtype === 'category_total' && ['purchaseprice'].includes(column.key)">
                    {{ "" }}
                  </template>
                  <template v-else-if="moneyColumns.includes(column.key)">
                    <span class="money-text">{{ formatCurrency(record[column.key]) }}</span>
                  </template>
                  <template v-else-if="qtyColumns.includes(column.key)">
                    <span class="qty-text">{{ formatNumber(record[column.key]) }}</span>
                  </template>
                  <template v-else-if="column.key === 'categoryname'">
                    <span class="category-pill">{{ record.categoryname || "-" }}</span>
                  </template>
                  <template v-else>
                    {{ displayValue(record[column.dataIndex]) }}
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import ReportDatePresets from "@/components/report/ReportDatePresets.vue";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const itemwiseColumns = [
  { title: "#", key: "slno", width: 70 },
  { title: "Date", dataIndex: "invoicedate", key: "invoicedate" },
  { title: "Bill No", dataIndex: "billno", key: "billno" },
  { title: "Invoice No", dataIndex: "invoiceno", key: "invoiceno" },
  { title: "Item", dataIndex: "itemname", key: "itemname" },
  { title: "Supplier", dataIndex: "vendorname", key: "vendorname" },
  { title: "GSTIN", dataIndex: "gstin", key: "gstin" },
  { title: "Lorry No", dataIndex: "transportmode", key: "transportmode" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Purchase Price", dataIndex: "purchaseprice", key: "purchaseprice", align: "right" },
  { title: "Bill Amount", dataIndex: "amount", key: "amount", align: "right" },
  { title: "MRP", dataIndex: "saleprice", key: "saleprice", align: "right" },
  { title: "MRP Value", dataIndex: "saleamount", key: "saleamount", align: "right" },
];

const categorywiseColumns = [
  { title: "#", key: "slno", width: 70 },
  { title: "Category", dataIndex: "categoryname", key: "categoryname" },
  { title: "Date", dataIndex: "invoicedate", key: "invoicedate" },
  { title: "Item", dataIndex: "itemname", key: "itemname" },
  { title: "Supplier", dataIndex: "vendorname", key: "vendorname" },
  { title: "GSTIN", dataIndex: "gstin", key: "gstin" },
  { title: "Invoice No", dataIndex: "invoiceno", key: "invoiceno" },
  { title: "Bill No", dataIndex: "billno", key: "billno" },
  { title: "Lorry No", dataIndex: "transportmode", key: "transportmode" },
  { title: "Qty", dataIndex: "quantity", key: "quantity", align: "right" },
  { title: "Purchase Price", dataIndex: "purchaseprice", key: "purchaseprice", align: "right" },
];

export default {
  name: "purchasereport",
  components: { ReportDatePresets },
  data() {
    return {
      title: "Purchase Report",
      title1: "Billwise, itemwise, and categorywise purchases",
      rawRows: [],
      tablePage: 1,
      tablePageSize: 50,
      billPage: 1,
      billPageSize: 25,
      categories: [],
      loading: false,
      filters: {
        startdate: this.defaultStartDate(),
        enddate: this.today(),
        categoryid: "",
        groupBy: "itemwise",
      },
      exportJob: {
        job_id: "",
        status: "",
        export_format: "",
        filename: "",
        error: "",
      },
      exportPoller: null,
      moneyColumns: ["purchaseprice", "amount", "saleprice", "saleamount"],
      qtyColumns: ["quantity", "itemcount"],
    };
  },
  computed: {
    categoryOptions() {
      return [
        { value: "", text: "All Categories" },
        ...this.categories.map((category) => ({
          value: category.categoryid,
          text: category.categoryname,
        })),
      ];
    },
    groupByOptions() {
      return [
        { value: "itemwise", text: "Item Wise" },
        { value: "billwise", text: "Bill Wise" },
        { value: "categorywise", text: "Category Wise" },
      ];
    },
    columns() {
      if (this.filters.groupBy === "categorywise") return categorywiseColumns;
      return itemwiseColumns;
    },
    pagedBills() {
      if (this.filters.groupBy !== "billwise") return [];
      return this.sortedBills.slice(this.billOffset, this.billOffset + this.billPageSize);
    },
    sortedBills() {
      // Bills in date order, then by bill number (dates arrive as DD/MM/YYYY).
      const key = (bill) => String(bill.invoicedate || "").split("/").reverse().join("-");
      return [...this.displayRows].sort((a, b) => key(a).localeCompare(key(b)) || String(a.billno || "").localeCompare(String(b.billno || "")));
    },
    billOffset() {
      return (this.billPage - 1) * this.billPageSize;
    },
    tablePagination() {
      // Only one page of rows is drawn; totals and exports still cover the whole period.
      const setPage = (page, pageSize) => {
        this.tablePage = pageSize !== this.tablePageSize ? 1 : page;
        this.tablePageSize = pageSize;
      };
      return {
        current: this.tablePage,
        pageSize: this.tablePageSize,
        showSizeChanger: true,
        pageSizeOptions: ["25", "50", "100", "200"],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total.toLocaleString("en-IN")} rows`,
        onChange: setPage,
        onShowSizeChange: setPage,
      };
    },
    displayRows() {
      if (this.filters.groupBy === "billwise") {
        const grouped = new Map();
        this.rawRows.forEach((row) => {
          const key = [row.purchaseid, row.billno, row.invoicedate, row.vendorname || ""].join("|");
          if (!grouped.has(key)) {
            grouped.set(key, {
              rowkey: key,
              invoicedate: row.invoicedate,
              billno: row.billno,
              invoiceno: row.invoiceno,
              transportmode: row.transportmode,
              vendorname: row.vendorname,
              gstin: row.gstin,
              address: row.address,
              itemcount: 0,
              itemlines: [],
              quantity: 0,
              amount: 0,
              saleamount: 0,
              total: 0,
              netamount: row.netamount,
            });
          }
          const bill = grouped.get(key);
          bill.itemcount += 1;
          bill.itemlines.push({
            itemcode: row.itemcode,
            itemname: row.itemname,
            hsncode: row.hsncode,
            quantity: row.quantity,
            purchaseprice: row.purchaseprice,
            amount: row.amount,
            saleprice: row.saleprice,
            saleamount: row.saleamount,
          });
          bill.quantity += Number(row.quantity || 0);
          bill.amount += Number(row.amount || 0);
          bill.saleamount += Number(row.saleamount || 0);
        });
        return Array.from(grouped.values()).map((row) => ({
          ...row,
          total: !this.filters.categoryid && row.netamount !== null && row.netamount !== undefined
            ? Number(row.netamount)
            : row.amount,
        }));
      }
      if (this.filters.groupBy === "itemwise") {
        return this.numberRows(this.buildItemRows(this.rawRows));
      }
      return this.numberRows(this.buildCategoryRows(this.rawRows));
    },
    summaryRowCount() {
      if (this.filters.groupBy === "billwise") return this.displayRows.length;
      return this.displayRows.filter((row) => row.rowtype === "item" || !row.rowtype).length;
    },
    summary() {
      const rowsToSum = this.displayRows.filter((row) => !row.rowtype || row.rowtype === "item" || this.filters.groupBy === "billwise");
      return rowsToSum.reduce(
        (acc, row) => {
          acc.quantity += Number(row.quantity || 0);
          acc.amount += Number(this.filters.groupBy === "billwise" ? row.total : row.amount || 0);
          acc.saleamount += Number(row.saleamount || 0);
          return acc;
        },
        { quantity: 0, amount: 0, saleamount: 0 }
      );
    },
    exportLoading() {
      return ["queued", "running"].includes(this.exportJob.status);
    },
    exportStatusTitle() {
      if (this.exportJob.status === "completed") return "Export ready";
      if (this.exportJob.status === "failed") return "Export failed";
      if (this.exportLoading) return "Export is being generated";
      return "Export";
    },
    exportStatusText() {
      if (this.exportJob.status === "completed") return this.exportJob.filename || "Your file is ready to download.";
      if (this.exportJob.status === "failed") return this.exportJob.error || "Something went wrong while generating the file.";
      if (this.exportLoading) return `Preparing ${this.exportJob.export_format === "pdf" ? "PDF" : "Excel"} with the selected filters.`;
      return "";
    },
  },
  watch: {
    "filters.groupBy"() {
      this.tablePage = 1;
      this.billPage = 1;
    },
  },
  created() {
    this.fetchCategories();
    this.generateReport();
  },
  beforeUnmount() {
    this.clearExportPoller();
  },
  methods: {
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    defaultStartDate() {
      const d = new Date();
      d.setDate(1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    async fetchCategories() {
      try {
        const response = await axios.get("/v1/category");
        this.categories = response.data || [];
      } catch (error) {
        console.error("Failed to load categories", error);
      }
    },
    async generateReport() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/purchasereport", {
          params: {
            startdate: this.filters.startdate,
            enddate: this.filters.enddate,
            categoryid: this.filters.categoryid || undefined,
            group_by: "itemwise",
          },
        });
        this.rawRows = Object.freeze(response.data || []);
        this.tablePage = 1;
        this.billPage = 1;
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to load purchase report",
          text: error.response?.data?.detail || error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    applyPeriod({ from, to }) {
      this.filters.startdate = from;
      this.filters.enddate = to;
      this.generateReport();
    },
    resetFilters() {
      this.filters = {
        startdate: this.defaultStartDate(),
        enddate: this.today(),
        categoryid: "",
        groupBy: "itemwise",
      };
      this.rawRows = [];
      this.generateReport();
    },
    clearExportPoller() {
      if (this.exportPoller) {
        window.clearInterval(this.exportPoller);
        this.exportPoller = null;
      }
    },
    async startExport(exportFormat) {
      this.clearExportPoller();
      try {
        const response = await axios.post("/v1/purchasereport/export", null, {
          params: {
            startdate: this.filters.startdate,
            enddate: this.filters.enddate,
            categoryid: this.filters.categoryid || undefined,
            group_by: this.filters.groupBy,
            export_format: exportFormat,
          },
        });
        this.exportJob = { ...this.exportJob, ...response.data, error: "" };
        this.pollExportJob(this.exportJob.job_id);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to start export",
          text: error.response?.data?.detail || error.message,
        });
      }
    },
    async pollExportJob(jobId) {
      await this.fetchExportJob(jobId);
      this.exportPoller = window.setInterval(async () => {
        const finished = await this.fetchExportJob(jobId);
        if (finished) this.clearExportPoller();
      }, 2000);
    },
    async fetchExportJob(jobId) {
      try {
        const response = await axios.get(`/v1/purchasereport/export/${jobId}`);
        this.exportJob = { ...this.exportJob, ...response.data };
        return ["completed", "failed"].includes(this.exportJob.status);
      } catch (error) {
        this.clearExportPoller();
        this.exportJob = { ...this.exportJob, status: "failed", error: error.response?.data?.detail || error.message };
        return true;
      }
    },
    async downloadExport() {
      if (!this.exportJob.job_id) return;
      try {
        const response = await axios.get(`/v1/purchasereport/export/${this.exportJob.job_id}/download`, { responseType: "blob" });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = this.exportJob.filename || `purchase_report.${this.exportJob.export_format === "pdf" ? "pdf" : "xlsx"}`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Download failed",
          text: error.response?.data?.detail || error.message,
        });
      }
    },
    buildCategoryRows(sourceRows) {
      const rows = [];
      const groups = new Map();
      sourceRows.forEach((row) => {
        const key = row.categoryname || "Uncategorized";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(row);
      });

      Array.from(groups.keys())
        .sort((a, b) => a.localeCompare(b))
        .forEach((categoryName) => {
          const groupRows = groups.get(categoryName) || [];
          rows.push({ rowkey: `header-${categoryName}`, rowtype: "category_header", categoryname: categoryName });

          let qty = 0;
          let amount = 0;
          let saleamount = 0;

          groupRows.forEach((row) => {
            rows.push({ ...row, rowtype: "item" });
            qty += Number(row.quantity || 0);
            amount += Number(row.amount || 0);
            saleamount += Number(row.saleamount || 0);
          });

          rows.push({
            rowkey: `total-${categoryName}`,
            rowtype: "category_total",
            categoryname: "",
            itemname: `Total - ${categoryName}`,
            quantity: qty,
            purchaseprice: null,
            amount,
            saleamount,
          });
        });

      return rows;
    },
    buildItemRows(sourceRows) {
      const rows = [];
      const groups = new Map();
      sourceRows.forEach((row) => {
        const key = row.itemname || "Item";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(row);
      });

      Array.from(groups.keys())
        .sort((a, b) => a.localeCompare(b))
        .forEach((itemName) => {
          const groupRows = groups.get(itemName) || [];
          rows.push({ rowkey: `header-${itemName}`, rowtype: "category_header", itemname: itemName });

          let qty = 0;
          let amount = 0;
          let saleamount = 0;

          groupRows.forEach((row) => {
            rows.push({ ...row, rowtype: "item" });
            qty += Number(row.quantity || 0);
            amount += Number(row.amount || 0);
            saleamount += Number(row.saleamount || 0);
          });

          rows.push({
            rowkey: `total-${itemName}`,
            rowtype: "category_total",
            categoryname: "",
            itemname: `Total - ${itemName}`,
            quantity: qty,
            purchaseprice: null,
            amount,
            saleamount,
          });
        });

      return rows;
    },
    rowSerialNumber(record) {
      return record.slno || "";
    },
    numberRows(rows) {
      // Serial numbers for the item rows, set once when the rows are built.
      let serial = 0;
      rows.forEach((row) => {
        if (!row.rowtype || row.rowtype === "item") {
          serial += 1;
          row.slno = serial;
        }
      });
      return rows;
    },
    rowClassName(record) {
      if (record.rowtype === "category_header") return "category-header-row";
      if (record.rowtype === "category_total") return "category-total-row";
      return "";
    },
    formatNumber(value) {
      // Quantities: whole numbers without ".00", fractions up to 2 decimals.
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    displayValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
  },
};
</script>

<style scoped src="./report-theme.css"></style>
<style scoped>
.billwise-bill {
  page-break-inside: avoid;
}
.billwise-bill-header {
  display: grid;
  grid-template-columns: 36px 190px minmax(140px, 1fr) minmax(190px, 1.4fr) minmax(180px, 1.2fr);
  gap: 8px;
  align-items: start;
  padding: 7px 10px 5px;
  font-weight: 700;
}
.bill-address {
  grid-column: 4 / 6;
  text-align: right;
}
.bill-index {
  text-align: center;
}
.bill-vendor {
  text-transform: uppercase;
}
.bill-meta {
  text-align: right;
}
.billwise-items th:nth-child(1) { width: 11%; }
.billwise-items th:nth-child(2) { width: 28%; }
.billwise-items th:nth-child(3) { width: 8%; }
.billwise-items th:nth-child(4) { width: 8%; }
.billwise-items th:nth-child(5) { width: 11%; }
.billwise-items th:nth-child(6) { width: 12%; }
.billwise-items th:nth-child(7) { width: 10%; }
.billwise-items th:nth-child(8) { width: 12%; }
.billwise-items .numeric {
  text-align: right;
  white-space: nowrap;
}
.billwise-items tfoot td:first-child {
  text-align: right;
}
@media (max-width: 992px) {
  .report-header-bar {
    flex-direction: column;
  }
  .export-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .export-status {
    flex-direction: column;
    align-items: flex-start;
  }
  .billwise-sheet {
    overflow-x: auto;
  }
  .billwise-bill {
    min-width: 840px;
  }
}
</style>
